/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  AUTO_ACCEPT_LIVE_CALL: 'transition/auto-accept-live-call',
  CUSTOMER_JOINS: 'transition/customer-joins',
PROVIDER_JOINS_AFTER_CUSTOMER: 'transition/provider-joins-after-customer',
PROVIDER_JOINS_AFTER_CUSTOMER_OFFLINE: 'transition/provider-joins-after-customer-offline',
SEND_REMINDER_TO_PROVIDER: 'transition/send-reminder-to-provider',
PROVIDER_JOINS_AFTER_REMINDER_SENT_TO_CUSTOMER: 'transition/provider-joins-after-reminder-sent-to-customer',
PROVIDER_JOINS_AFTER_REMINDER_SENT_TO_CUSTOMER_OFFLINE: 'transition/provider-joins-after-reminder-sent-to-customer-offline',
PROVIDER_JOINS: 'transition/provider-joins',
CUSTOMER_JOINS_AFTER_PROVIDER: 'transition/customer-joins-after-provider',
CUSTOMER_JOINS_AFTER_PROVIDER_OFFLINE: 'transition/customer-joins-after-provider-offline',
SEND_REMINDER_TO_CUSTOMER: 'transition/send-reminder-to-customer',
CUSTOMER_JOINS_AFTER_REMINDER_SENT_TO_PROVIDER: 'transition/customer-joins-after-reminder-sent-to-provider',
CUSTOMER_JOINS_AFTER_REMINDER_SENT_TO_PROVIDER_OFFLINE: 'transition/customer-joins-after-reminder-sent-to-provider-offline',
START_LIVE_CALL: 'transition/start-live-call',
END_LIVE_CALL: 'transition/end-live-call',
  PROVIDER_VIDEO_UPLOADED: "transition/provider-uploaded-video",
  // When the Customer book a RUSH Delivery Service, 
  // it is transitioned with the accept state
  ACCEPT_RUSH_DELIVERY: 'transition/rush-delivery',
  MOVE_TO_DELAY_RUSH_DELIVERY: 'transition/move-to-delay-rush-delivery',
  COMPLETE_RUSH_DELIVERY: 'transition/complete-rush-delivery',
  COMPLETE_AFTER_DELAY: 'transition/complete-after-delay',
  INQUIRE_RUSH_DELIVERY: 'transition/inquire-rush-delivery',
  PROVIDER_CAME_BACK_ONLINE: 'transition/provider-came-back-online',


  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  //Customer may book 'within-24-hours' deliverable service
  //Such service require different states for different functionality
  //Provider and Operator both can accpet the serive request
  ACCEPT_24HOURS_DELIVERY: 'transition/accept-24hours-delivery',
  OPERATOR_ACCEPT_24HOURS_DELIVERY: 'transition/operator-accept-24hours-delivery',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',
  CANCEL_24HOURS_DELIVERY: 'transition/cancel-24hours-delivery',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',
  COMPLETE_24HOURS_DELIVERY: 'transition/complete-24hours-delivery',
  OPERATOR_COMPLETE_24HOURS_DELIVERY: 'transition/operator-complete-24hours-delivery',

  //If the 'within-24hrs' deliverable service is not
  //delivered within 20hrs the respective transaction
  //will move to 'yellow zone' (warning zone)
  MOVE_TO_YELLOW_ZONE: 'transition/move-to-yellow-zone',

  //Transtation may be completed during this zone too.
  COMPLETE_AFTER_YELLOW_ZONE: 'transition/complete-after-yellow-zone',
  OPERATOR_COMPLETE_AFTER_YELLOW_ZONE: 'transition/operator-complete-after-yellow-zone',

  //Transaction may be canceled during this zone too.
  CANCEL_AFTER_YELLOW_ZONE: 'transition/cancel-after-yellow-zone',

  //If the 'within-24hrs' services not delivered
  //till 23 hours past, the respective transaction
  //will move to the red-zone (danger zone)
  MOVE_TO_RED_ZONE: 'transition/move-to-red-zone',

  //Transtation may be completed during this zone too.
  COMPLETE_AFTER_RED_ZONE: 'transition/complete-after-red-zone',
  OPERATOR_COMPLETE_AFTER_RED_ZONE: 'transition/operator-complete-after-red-zone',

  //Transaction may be canceled during this zone too.
  CANCEL_AFTER_RED_ZONE: 'transition/cancel-after-red-zone',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};


/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted', 
  EXPIRED: 'expired',
  ACCEPTED_24HOURS_DELIVERY: 'accepted-24hours-delivery',
  BOTH_PARTIES_JOINED: 'both-parties-joined',
  STARTED_LIVE_CALL: 'started-live-call',
  PROVIDER_JOINED: 'provider-joined',
  CUSTOMER_JOINED: 'customer-joined',
  SENT_REMINDER_TO_PROVIDER: 'sent-reminder-to-provider',
  SENT_REMINDER_TO_CUSTOMER: 'sent-reminder-to-customer',
  CANCELED: 'canceled',
  DELIVERED: 'delivered',
  YELLOW_ZONE: 'yellow-zone',
  RED_ZONE: 'red-zone',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  DELAY_RUSH_DELIVERY: 'delay-rush-delivery',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.INQUIRE_RUSH_DELIVERY]: states.INQUIRY,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.PROVIDER_CAME_BACK_ONLINE]: states.INQUIRY,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.AUTO_ACCEPT_LIVE_CALL]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.ACCEPT_24HOURS_DELIVERY]: states.ACCEPTED_24HOURS_DELIVERY,
        [transitions.ACCEPT_RUSH_DELIVERY]: states.ACCEPTED
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
        [transitions.COMPLETE_RUSH_DELIVERY]: states.DELIVERED,
        [transitions.MOVE_TO_DELAY_RUSH_DELIVERY]: states.DELAY_RUSH_DELIVERY, 
        [transitions.PROVIDER_JOINS]: states.PROVIDER_JOINED,
        [transitions.CUSTOMER_JOINS]: states.CUSTOMER_JOINED,
    
      },
    },

    [states.PROVIDER_JOINED]: {
      on: {
        [transitions.SEND_REMINDER_TO_CUSTOMER]:
          states.SENT_REMINDER_TO_CUSTOMER,
        [transitions.CUSTOMER_JOINS_AFTER_PROVIDER]: states.BOTH_PARTIES_JOINED,
        [transitions.CUSTOMER_JOINS_AFTER_PROVIDER_OFFLINE]:
          states.CUSTOMER_JOINED,
      },
    },

    [states.CUSTOMER_JOINED]: {
      on: {
        [transitions.SEND_REMINDER_TO_PROVIDER]:
          states.SENT_REMINDER_TO_PROVIDER,
        [transitions.PROVIDER_JOINS_AFTER_CUSTOMER]: states.BOTH_PARTIES_JOINED,
        [transitions.PROVIDER_JOINS_AFTER_CUSTOMER_OFFLINE]:
          states.PROVIDER_JOINED,
      },
    },

    [states.SENT_REMINDER_TO_PROVIDER]: {
      on: {
        [transitions.PROVIDER_JOINS_AFTER_REMINDER_SENT_TO_CUSTOMER]:
          states.BOTH_PARTIES_JOINED,
        [transitions.PROVIDER_JOINS_AFTER_REMINDER_SENT_TO_CUSTOMER_OFFLINE]:
          states.PROVIDER_JOINED,
      },
    },

    [states.SENT_REMINDER_TO_CUSTOMER]: {
      on: {
        [transitions.CUSTOMER_JOINS_AFTER_REMINDER_SENT_TO_PROVIDER]:
          states.BOTH_PARTIES_JOINED,
        [transitions.CUSTOMER_JOINS_AFTER_REMINDER_SENT_TO_PROVIDER_OFFLINE]:
          states.CUSTOMER_JOINED,
      },
    },

    [states.BOTH_PARTIES_JOINED]: {
      on: {
        [transitions.START_LIVE_CALL]: states.STARTED_LIVE_CALL,
      },
    },

    [states.STARTED_LIVE_CALL]: {
      on: {
        [transitions.END_LIVE_CALL]: states.DELIVERED,
      },
    },

    [states.DELAY_RUSH_DELIVERY]: {
      on: {
        [transitions.COMPLETE_AFTER_DELAY]: states.DELIVERED,
      },
    },
    [states.ACCEPTED_24HOURS_DELIVERY]: {
      on: {
        [transitions.CANCEL_24HOURS_DELIVERY]: states.CANCELED,
        [transitions.COMPLETE_24HOURS_DELIVERY]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_24HOURS_DELIVERY]: states.DELIVERED,
        [transitions.MOVE_TO_YELLOW_ZONE]: states.YELLOW_ZONE
      }
    },
    [states.YELLOW_ZONE]: {
      on: {
        [transitions.CANCEL_AFTER_YELLOW_ZONE]: states.CANCELED,
        [transitions.COMPLETE_AFTER_YELLOW_ZONE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_AFTER_YELLOW_ZONE]: states.DELIVERED,
        [transitions.MOVE_TO_RED_ZONE]: states.RED_ZONE
      }
    },
    [states.RED_ZONE]: {
      on: {
        [transitions.CANCEL_AFTER_RED_ZONE]: states.CANCELED,
        [transitions.COMPLETE_AFTER_RED_ZONE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_AFTER_RED_ZONE]: states.DELIVERED,
      }
    },
    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.PROVIDER_VIDEO_UPLOADED]: states.DELIVERED,
        
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.AUTO_ACCEPT_LIVE_CALL,
    transitions.CUSTOMER_JOINS,
    transitions.PROVIDER_JOINS,
    transitions.PROVIDER_JOINS_AFTER_CUSTOMER,
    transitions.PROVIDER_JOINS_AFTER_CUSTOMER_OFFLINE,
    transitions.SEND_REMINDER_TO_PROVIDER,
    transitions.PROVIDER_JOINS_AFTER_REMINDER_SENT_TO_CUSTOMER,
    transitions.PROVIDER_JOINS_AFTER_REMINDER_SENT_TO_CUSTOMER_OFFLINE,
    transitions.SEND_REMINDER_TO_CUSTOMER,
    transitions.CUSTOMER_JOINS_AFTER_PROVIDER,
    transitions.CUSTOMER_JOINS_AFTER_PROVIDER_OFFLINE,
    transitions.CUSTOMER_JOINS_AFTER_REMINDER_SENT_TO_PROVIDER,
    transitions.CUSTOMER_JOINS_AFTER_REMINDER_SENT_TO_PROVIDER_OFFLINE,
    transitions.START_LIVE_CALL,
    transitions.END_LIVE_CALL,
    transitions.CANCEL,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.ACCEPT_24HOURS_DELIVERY,
    transitions.COMPLETE_24HOURS_DELIVERY,
    transitions.COMPLETE_AFTER_DELAY,
    transitions.PROVIDER_VIDEO_UPLOADED,
    transitions.COMPLETE_RUSH_DELIVERY,
    transitions.ACCEPT_RUSH_DELIVERY,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY].includes(
    transition
  );
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
